import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaCamera, FaFilm, FaPenNib, FaUniversity, FaUser } from 'react-icons/fa';
import { helperFunction } from '../helpers/api_call_helper';
import { getEvents } from '../helpers/backend_helper';
import { normalizeEvent } from '../helpers/utils';

const getEventIcon = (emoji) => {
    switch (emoji) {
        case 'camera': return <FaCamera />;
        case 'film': return <FaFilm />;
        case 'university': return <FaUniversity />;
        case 'pen': return <FaPenNib />;
        default: return <FaCamera />;
    }
};

const getEventsPayload = (response) => {
    const payload = response?.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.events)) return payload.events;
    if (Array.isArray(payload?.data?.events)) return payload.data.events;
    if (Array.isArray(payload)) return payload;
    return [];
};

export default function CelebrityEvents({ preview = false }) {
    const navigate = useNavigate();
    const [apiEvents, setApiEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasLoadedEvents, setHasLoadedEvents] = useState(false);
    const [fetchError, setFetchError] = useState('');

    useEffect(() => {
        let mounted = true;
        setHasLoadedEvents(false);

        helperFunction(
            async () => {
                const response = await getEvents({ limit: preview ? 3 : 100 });
                const nextEvents = getEventsPayload(response).map(normalizeEvent);
                if (mounted) {
                    setApiEvents(nextEvents);
                    setHasLoadedEvents(true);
                }
                return response;
            },
            setLoading,
            undefined,
            (message) => {
                if (mounted) {
                    setFetchError(message);
                    setHasLoadedEvents(true);
                }
            },
        );

        return () => {
            mounted = false;
        };
    }, [preview]);

    const visibleEvents = preview ? apiEvents.slice(0, 3) : apiEvents;

    const renderEventCard = (event, idx) => {
        const hasGallery = event.gallery && event.gallery.length > 0;
        const coverImg = hasGallery ? event.gallery[0] : null;
        const detailPath = `/event/${event.slug || event.id}`;

        return (
            <div
                key={event.id}
                className="event-card reveal in-view"
                style={{ transitionDelay: `${idx * 0.1}s` }}
                onClick={() => navigate(detailPath)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(detailPath)}
                aria-label={`View ${event.title} event`}
            >
                <div className="event-image">
                    {coverImg ? (
                        <img
                            src={coverImg}
                            alt={event.imageAlt || event.title}
                            loading="lazy"
                            decoding="async"
                            width="360"
                            height="240"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <span style={{ fontSize: '4.5rem', opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            {getEventIcon(event.imageIcon)}
                        </span>
                    )}
                    <div className="event-image-overlay" />
                    <span className="event-date-badge">{event.badgeDate}</span>
                </div>

                <div className="event-info">
                    <h3 className="event-title">{event.title}</h3>

                    <div className="event-celebrity">
                        <div className="celebrity-avatar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaUser style={{ opacity: 0.6 }} />
                        </div>
                        <div className="celebrity-info">
                            <h4>{event.celebrity.name}</h4>
                            <p className="celebrity-title">{event.celebrity.title}</p>
                        </div>
                    </div>

                    <p className="event-quote">"{event.quote}"</p>

                    {hasGallery && event.gallery.length > 1 && (
                        <div className="event-gallery-strip">
                            {event.gallery.slice(0, 3).map((img, i) => (
                                <img
                                    key={i}
                                    src={img}
                                    alt=""
                                    className="event-gallery-thumb"
                                    loading="lazy"
                                    decoding="async"
                                    width="56"
                                    height="56"
                                />
                            ))}
                            {event.gallery.length > 3 && (
                                <div className="event-gallery-more">
                                    +{event.gallery.length - 3}
                                </div>
                            )}
                        </div>
                    )}

                    <button
                        className="view-event-details"
                        onClick={(e) => { e.stopPropagation(); navigate(detailPath); }}
                    >
                        View Full Event
                    </button>
                </div>
            </div>
        );
    };

    const renderEventSkeletonCards = () => (
        Array.from({ length: 3 }).map((_, index) => (
            <div className="event-card reveal in-view detail-skeleton" key={`event-skeleton-${index}`}>
                <div className="event-image detail-skeleton-block" />
                <div className="event-info detail-skeleton-stack">
                    <div className="detail-skeleton-line detail-skeleton-heading" />
                    <div className="detail-skeleton-row">
                        <div className="detail-skeleton-circle" />
                        <div className="detail-skeleton-line detail-skeleton-meta-value" />
                    </div>
                    <div className="detail-skeleton-line" />
                    <div className="detail-skeleton-line detail-skeleton-wide" />
                    <div className="detail-skeleton-line detail-skeleton-button" />
                </div>
            </div>
        ))
    );

    return (
        <section className={`events-section ${preview ? 'home-preview-section' : 'full-list-section'}`} id="events">
            <div className="section">
                <div className="section-header reveal in-view section-header-with-nav">
                    <div className="header-content">
                        <div className="section-label">Literary Events</div>
                        <h2 className="section-title">Celebrity Book Launches</h2>
                        <p className="section-subtitle">
                            Presenting books to Pakistan's most influential personalities
                        </p>
                    </div>
                    {preview && (
                        <button className="preview-more-btn" type="button" onClick={() => navigate('/events')}>
                            More Events <FaArrowRight />
                        </button>
                    )}
                </div>

                <div className="content-preview-grid">
                    {(!hasLoadedEvents || loading) && !apiEvents.length && renderEventSkeletonCards()}
                    {hasLoadedEvents && !loading && !apiEvents.length && (
                        <div className="event-card reveal in-view" style={{ padding: '2rem', color: 'var(--text-dim)' }}>
                            {fetchError || 'No events available yet.'}
                        </div>
                    )}
                    {visibleEvents.map((event, idx) => renderEventCard(event, idx))}
                </div>
            </div>
        </section>
    );
}
